// Where a view puts the camera. Neurons' close-up fits the cells its run draws into the part of the
// canvas the panels leave clear, from the side it asks for; any other view (and the close-up until its
// run is in) has its own orbit, or its camera preset framing what it isolates.

import type { Orbit, Vec3 } from '../viewport/api.ts';
import type { ScenePreset } from '../scenarios/index.ts';
import { lastCamera } from '../viewport/events.ts';
import { drawsLayer, drawsSlot, placedIn } from '../viewport/draws.ts';
import { slotsOf, type SceneState } from './scene.ts';
import type { CellsHeader } from '../data/sims/types.ts';
import type { AtlasState } from './store.ts';

/** The app's lens, ° (engines/wgpu `look::APP_FOV_DEG`), until a camera event says. */
const APP_FOV_DEG = 28;

/** The share of the clear area's half-width and half-height the cells may take. */
export const MARGIN = 0.85;

export interface ViewSize {
  /** The canvas, CSS px. */
  width: number;
  height: number;
  /** What the panels cover of it, CSS px. */
  insets: { top: number; right: number; bottom: number; left: number };
  fovYDeg: number;
}

/**
 * The orbit that fits `points` (xyz triples, mm) into the canvas the panels leave clear, from yaw and
 * pitch as the engine reads them (the eye along (cos p sin y, cos p cos y, sin p) from the target). The
 * engine fits its lens to that clear area, with a shift (ba-render `proj_matrix`): the vertical field of
 * view spans the clear height, and the target sits at its centre. The target is the middle of the
 * points' extent across, up and along the view; each point's depth counts, so one nearer the camera gets
 * the room it needs.
 */
export function fitOrbit(points: ArrayLike<number>, yawDeg: number, pitchDeg: number, view: ViewSize, margin = MARGIN): Orbit | null {
  const n = Math.floor(points.length / 3);
  if (!n) return null;
  const yaw = (yawDeg * Math.PI) / 180;
  const pitch = (Math.max(-89, Math.min(89, pitchDeg)) * Math.PI) / 180;
  const f: Vec3 = [-Math.cos(pitch) * Math.sin(yaw), -Math.cos(pitch) * Math.cos(yaw), -Math.sin(pitch)];
  // Right is forward × up (+z), and up is right × forward (ba-render `Orbit::basis`).
  const len = Math.hypot(f[1], f[0]);
  const r: Vec3 = len > 1e-9 ? [f[1] / len, -f[0] / len, 0] : [1, 0, 0];
  const u: Vec3 = [r[1] * f[2] - r[2] * f[1], r[2] * f[0] - r[0] * f[2], r[0] * f[1] - r[1] * f[0]];
  const axes = [r, u, f];
  // The points in the camera's axes, and the middle of their extent along each.
  const lo = [Infinity, Infinity, Infinity];
  const hi = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < n; i++)
    for (let k = 0; k < 3; k++) {
      const a = axes[k]!;
      const v = a[0] * points[3 * i]! + a[1] * points[3 * i + 1]! + a[2] * points[3 * i + 2]!;
      if (v < lo[k]!) lo[k] = v;
      if (v > hi[k]!) hi[k] = v;
    }
  const mid = [0, 1, 2].map((k) => (lo[k]! + hi[k]!) / 2);
  const c = [0, 1, 2].map((j) => mid[0]! * r[j]! + mid[1]! * u[j]! + mid[2]! * f[j]!) as Vec3;
  const cw = Math.max(1, view.width - view.insets.left - view.insets.right);
  const ch = Math.max(1, view.height - view.insets.top - view.insets.bottom);
  const t = Math.tan((view.fovYDeg * Math.PI) / 360);
  // A point x across and y up at depth D projects x / (D t) of the clear half-height from the centre:
  // it needs D ≥ x / (t · aspect · margin) and D ≥ y / (t · margin), with D the distance plus its depth.
  let d = 1e-3;
  for (let i = 0; i < n; i++) {
    const q = [points[3 * i]! - c[0], points[3 * i + 1]! - c[1], points[3 * i + 2]! - c[2]];
    const x = Math.abs(r[0] * q[0]! + r[1] * q[1]! + r[2] * q[2]!);
    const y = Math.abs(u[0] * q[0]! + u[1] * q[1]! + u[2] * q[2]!);
    const z = f[0] * q[0]! + f[1] * q[1]! + f[2] * q[2]!;
    d = Math.max(d, (x * ch) / (t * cw * margin) - z, y / (t * margin) - z, -1.05 * z);
  }
  return { targetMm: c, yawDeg, pitchDeg, distanceMm: d };
}

type FramingState = SceneState & Pick<AtlasState, 'engine' | 'catalogue' | 'insets'>;

/** The space a cells run's points are in (SimSlot.points): its placement's, else its own. */
const pointsSpace = (h: CellsHeader) => h.placement?.space ?? h.space;

/** The cells run the viewport draws in this scene, with its bounds: none where it draws none. In a run's own frame (the
 *  column's close-up) the frame's space is the run's, and the points are as stored. */
export function drawnCells(s: FramingState): { path: string; points: Float32Array } | null {
  const space = s.catalogue?.pack.space;
  const e = s.engine;
  const fits = (h: CellsHeader) => placedIn(h, space) && (!space || !pointsSpace(h) || pointsSpace(h) === space);
  const x = slotsOf(s).find((y) => y.format === 'cells' && !y.appOnly && !!y.points && drawsSlot(e.api, e.sims, y) && drawsLayer(e.layers, 'arbors') && !e.undrawn?.[y.path] && fits(y.header as CellsHeader));
  return x?.points ? { path: x.path, points: x.points } : null;
}

/** The orbit Neurons' close-up takes on the cells the scene's run draws, at the canvas's size now; null when none is drawn. */
export function cellsOrbit(s: FramingState, side: { yawDeg: number; pitchDeg: number }): Orbit | null {
  const cells = drawnCells(s);
  if (!cells) return null;
  const cam = lastCamera.current;
  const width = cam?.width ?? globalThis.innerWidth ?? 0;
  const height = cam?.height ?? globalThis.innerHeight ?? 0;
  if (!(width > 0 && height > 0)) return null;
  return fitOrbit(cells.points, side.yawDeg, side.pitchDeg, { width, height, insets: s.insets, fovYDeg: cam?.fovYDeg ?? APP_FOV_DEG });
}

/** The camera a view asks for: the close-up's cells where they're drawn, else its orbit, else its preset framing what it isolates. */
export function presetCamera(s: FramingState, p: ScenePreset, isolate = p.isolate): Pick<NonNullable<AtlasState['cameraRequest']>, 'orbit' | 'preset' | 'frame' | 'direction'> {
  const cells = p.frameCells ? cellsOrbit(s, p.frameCells) : null;
  if (cells) return { orbit: cells };
  if (p.orbit) return { orbit: p.orbit };
  return { preset: p.camera, ...(isolate ? { frame: isolate, direction: p.camera } : {}) };
}
